<template>
  <div>
    <LoadingSpinner v-if="loading" />
    <div v-if="metaDataLoaded">
      <BrandingUpsell
        v-if="!canAccess"
        :brandingData="brandingData"
        :canUpgrade="canUpgrade"
        :hasTried="hasTried"
      />
      <PermissionGate v-else role="super-admin">
        <template v-slot:unauthorised>
          <VMessage
            type="warning"
            :fullwidth="true"
            class="p-partials-users__message-margin"
          >
            <template v-slot:icon>
              <ExclamationTriangleIcon />
            </template>
            <template v-slot:text>
              {{ $t('branding.permissionWarning') }}
            </template>
          </VMessage>
        </template>
        <div class="bg-whiteOverride px-base py-lg sm:p-lg border-1 border-solid border-border h-auto">
          <LowEnvelopeAllowanceWarning ec="branding" />
            <div
              v-if="currentPlan && currentPlan.id === 'free-trial'"
              class="px-sm md:px-lg mb-5"
            >
              <i18n path="upsells.branding.freeTrialSetBrandingExplainer">
                <template v-slot:link>
                  <a
                    href="https://www.signable.co.uk/plans/"
                    class="text-primary"
                    data-ec="plu-upsell"
                    data-et="free-trial-set-branding-explainer"
                    target="_blank"
                  >
                    <br> {{ $t('upsells.learnMoreMessage') }}
                  </a>
                </template>
              </i18n>
            </div>
            <div class="md:grid gap-x-lg px-base sm:px-lg grid-cols-pageBranding">
              <h3 class="bg-white rounded-lg px-base sm:px-lg py-4 m-0 -mx-base sm:-mx-lg col-span-full">
                {{ $t('branding.logos') }}
              </h3>

              <div>
                <p class="mt-lg">
                  {{ $t('branding.logosDescription') }}
                </p>
                <p class="mb-lg">
                  {{ $t('branding.logosDescription2') }}
                </p>
              </div>
              <div class="flex flex-wrap justify-around gap-lg my-lg md:flex-nowrap md:justify-end md:ml-0 3xl:justify-start">
                <BrandingOption
                  :title="$t('branding.primaryLogo')"
                  :action="() => showModal('logo')"
                  :actionText="$t('functional.upload')"
                  :reset="() => updateBrandingLogo('logo', null)"
                  :resetText="$t('functional.restoreDefault')"
                  actionEt="open_brand_logo_modal"
                  resetEt="reset_brand_logo"
                >
                  <div class="w-full h-full p-sm">
                    <div
                      class="w-full h-full bg-contain bg-center bg-no-repeat"
                      :style="`background-image: url('${branding.logo}')`"
                    />
                  </div>
                </BrandingOption>

                <BrandingOption
                  :title="$t('branding.secondaryLogo')"
                  :action="() => showModal('logoAlternate')"
                  :actionText="$t('functional.upload')"
                  :reset="() => updateBrandingLogo('logo_alternate', null)"
                  :resetText="$t('functional.restoreDefault')"
                  actionEt="open_brand_logo_alternate_modal"
                  resetEt="reset_brand_logo_alternate"
                >
                  <div
                    class="w-full h-full p-sm"
                    :style="{ 'background-color': branding.colour }"
                  >
                    <div
                      class="w-full h-full bg-contain bg-center bg-no-repeat"
                      :style="`background-image: url('${
                        branding.logo_alternate || branding.logo
                      }')`"
                    />
                  </div>
                </BrandingOption>
              </div>
            </div>

            <div class="md:grid gap-x-lg px-base sm:px-lg grid-cols-pageBranding">
              <h3 class="bg-white rounded-lg px-base sm:px-lg py-4 m-0 -mx-base sm:-mx-lg col-span-full">
                {{ $t('branding.colours') }}
              </h3>

              <p class="my-lg">{{ $t('branding.coloursDescription') }}</p>

              <div class="flex flex-wrap justify-around gap-lg my-lg md:flex-nowrap md:justify-end md:ml-0 3xl:justify-start">
                <BrandingOption
                  :title="$t('branding.primaryColour')"
                  :action="() => showModal('colour')"
                  :actionText="$t('functional.edit')"
                  :reset="() => updateBrandingColour('colour', null)"
                  :resetText="$t('functional.restoreDefault')"
                  actionEt="open_brand_colour_modal"
                  resetEt="reset_brand_colour"
                >
                  <div
                    class="flex justify-center items-center w-full h-full"
                    :style="{ 'background-color': branding.colour }"
                  >
                    <span
                      class="text-white font-semibold"
                      :class="{ 'text-black': colourIsLight }"
                    >
                      {{ branding.colour }}
                    </span>
                  </div>
                </BrandingOption>

                <BrandingOption
                  :title="$t('branding.secondaryColour')"
                  :action="() => showModal('colourAccent')"
                  :actionText="$t('functional.edit')"
                  :reset="() => updateBrandingColour('colour_accent', null)"
                  :resetText="$t('functional.remove')"
                  actionEt="open_brand_colour_accent_modal"
                  resetEt="reset_brand_colour_accent"
                >
                  <div
                    class="flex justify-center items-center w-full h-full"
                    :style="{ 'background-color': branding.colour_accent }"
                  >
                    <span
                      class="text-white font-semibold"
                      :class="{ 'text-black': colourAccentIsLight }"
                    >
                      {{ branding.colour_accent || 'not set' }}
                    </span>
                  </div>
                </BrandingOption>
              </div>
            </div>

            <div>
              <h3 class="bg-white rounded-lg px-base sm:px-lg py-4 m-0 col-span-full">
                {{ $t('branding.previews') }}
              </h3>

              <div class="flex flex-wrap justify-around my-lg sm:m-lg">
                <div class="flex flex-col w-full 2xl:w-1/2 2xl:max-w-screen-lg 2xl:pr-base">
                  <h4 class="!font-body">{{ $t('branding.previewEmail') }}</h4>

                  <iframe
                    v-if="previewEmailNoPointing"
                    :title="$t('branding.previewEmail')"
                    :srcdoc="previewEmailNoPointing"
                    sandbox
                    class="h-brandingPreview w-full border-1 border-border border-solid"
                  />
                  <p v-else>{{ $t('branding.previewEmailError') }}</p>
                </div>

                <div class="flex flex-col w-full mt-lg 2xl:w-1/2 2xl:max-w-screen-lg 2xl:pl-base 2xl:mt-0">
                  <h4 class="!font-body">{{ $t('branding.previewConfirmation') }}</h4>

                  <SignedPreview
                    :logo="branding.logo_alternate || branding.logo"
                    :colour="branding.colour"
                    :colourIsWhite="branding.colours.primaryIsWhite"
                    :colourAccent="branding.colour_accent"
                    :companyName="company.name"
                    class="h-brandingPreview w-full border-1 border-border border-solid"
                  />
                </div>
              </div>
            </div>

            <div class="md:grid gap-x-lg px-base sm:px-lg grid-cols-pageBranding">
              <h3 class="bg-white rounded-lg px-base sm:px-lg py-4 m-0 -mx-base sm:-mx-lg col-span-full">
                {{ $t('branding.emailTemplates') }}
              </h3>

              <div class="my-lg">
                <p>{{ $t('branding.updateEmailTemplatesExplainer') }}</p>
                <p>{{ $t('branding.advancedFeature') }}</p>
              </div>

              <div class="my-lg">
                <h4 class="!font-body">{{ $t('branding.customTemplates') }}</h4>
                <BrandingEmails :emails="emails" />
              </div>
            </div>

          <LogoModal
            v-if="modals.logo"
            logoType="primary"
            :currentLogo="branding.logo"
            backgroundColour="#fff"
            :update="(update) => updateBrandingLogo('logo', update)"
            @close="closeModal('logo')"
          />
          <LogoModal
            v-if="modals.logoAlternate"
            logoType="secondary"
            :currentLogo="branding.logo_alternate"
            :backgroundColour="branding.colour"
            :update="(update) => updateBrandingLogo('logo_alternate', update)"
            @close="closeModal('logoAlternate')"
          />
          <ColourModal
            v-if="modals.colour"
            colourType="primary"
            :initialColour="branding.colour"
            :update="(update) => updateBrandingColour('colour', update)"
            @close="() => closeModal('colour')"
          />
          <ColourModal
            v-if="modals.colourAccent"
            colourType="secondary"
            :initialColour="branding.colour_accent"
            :update="(update) => updateBrandingColour('colour_accent', update)"
            @close="() => closeModal('colourAccent')"
          />
        </div>
      </PermissionGate>
    </div>
  </div>
</template>

<script setup>
// @ts-check
import { computed, ref } from 'vue';
import { useActions, useGetters } from 'vuex-composition-helpers/dist';

import { EventBus } from '@/utils/event-bus';
import { useLightOrDark } from '@/composables/utils/colour/useContrastColour';
import { useBrandingUpsell } from '@/composables/branding';
import i18nlocalisation from '@/locales';

import LoadingSpinner from '@/components/deprecated/atoms/LoadingSpinner.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import BrandingEmails from '@/components/ui/branding/BrandingEmails';
import BrandingOption from '@/components/ui/branding/BrandingOption';
import ColourModal from '@/components/ui/branding/modals/ColourModal';
import LogoModal from '@/components/ui/branding/modals/LogoModal';
import SignedPreview from '@/components/ui/branding/SignedPreview';

import LowEnvelopeAllowanceWarning from '@/views/components/LowEnvelopeAllowanceWarning.vue';
import { BrandingUpsell } from '@/components/ui/branding/upsells/BrandingUpsell';
import { PermissionGate } from '@/components/gates/PermissionGate';
import { ExclamationTriangleIcon } from '@/components/icons/fontIcons';

const {
  company,
  branding,
  previewEmail,
  userHasRole,
  planHasFeature,
  emails,
  currentPlan,
} = useGetters({
  company: 'company/company',
  branding: 'branding/getBranding',
  previewEmail: 'branding/previewEmail',
  userHasRole: 'roles/userHasRole',
  planHasFeature: 'plan/planHasFeature',
  emails: 'emails/pagedResults',
  currentPlan: 'plans/current',
});
const {
  fetchBranding,
  fetchPreview,
  updateBranding,
  updateBrandingFile,
  fetchEmails,
} = useActions({
  fetchBranding: 'branding/GET',
  fetchPreview: 'branding/PREVIEW_EMAIL',
  updateBranding: 'branding/UPDATE',
  updateBrandingFile: 'branding/UPDATE_FILE',
  fetchEmails: 'emails/PAGINATE',
});

const {
  brandingData,
  canUpgrade,
  hasTried,
  canAccess,
  metaDataLoaded,
} = useBrandingUpsell();

const featureBranding = computed(() => planHasFeature.value('branding'));
const permissionBranding = computed(() => userHasRole.value('super-admin'));

const colourIsLight = useLightOrDark(computed(() => branding.value.colour));
const colourAccentIsLight = useLightOrDark(computed(() => branding.value.colour_accent));

const previewEmailNoPointing = computed(() => previewEmail.value && `<style>* {pointer-events: none !important}</style>${previewEmail.value}`);

const loading = ref(false);

const modals = ref({
  colour: false,
  colourAccent: false,
  logo: false,
  logoAlternate: false,
});
function showModal(modalToOpen) {
  modals.value[modalToOpen] = true;
}
function closeModal(modalToClose) {
  modals.value[modalToClose] = false;
}

async function getBranding() {
  if (featureBranding.value) {
    loading.value = true;

    await fetchBranding();

    if (permissionBranding.value) {
      await Promise.all([
        fetchPreview(),
        fetchEmails({ page: 1 }),
      ]);
    }

    loading.value = false;
  }
}

/**
 * Update brand colour
 *
 * @param {'colour'|'colour_accent'} colourToUpdate the colour attribute to update
 * @param {string|null} update the hex colour or null to reset to default
 */
async function updateBrandingColour(colourToUpdate, update) {
  await updateBranding({ [colourToUpdate]: update });

  fetchPreview();

  EventBus.$emit('renderGlobalMessage', {
    message: i18nlocalisation.t('branding.colourUpdatedMessage'),
    icon: 'check',
    type: 'success',
  });
}

/**
 * Update brand logo
 *
 * @param {'logo'|'logo_alternate'} logoToUpdate the logo attribute to update
 * @param {File|null} update the logo file or null to reset to default
 */
async function updateBrandingLogo(logoToUpdate, update) {
  if (update) {
    await updateBrandingFile({ [logoToUpdate]: update });
  } else {
    await updateBranding({ [logoToUpdate]: null });
  }

  fetchPreview();

  EventBus.$emit('renderGlobalMessage', {
    message: i18nlocalisation.t('branding.logoUpdatedMessage', {
      attribute: 'logo',
    }),
    icon: 'check',
    type: 'success',
  });
}

getBranding();
</script>
